import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { Block, CheckCircle, Recommend, RemoveRedEye, Schedule, Shield, Speed } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors, status } from '../../theme';
import { Card, RecommendationsSkeleton, fmt, getDisplayText, getDisplayTone, tdSx, thSx, trSx } from './shared';

const ICONS = {
  check_circle: CheckCircle,
  schedule: Schedule,
  block: Block,
  remove_red_eye: RemoveRedEye,
  shield: Shield,
  speed: Speed,
};

export default function RecommendationsSection({ isFirstLoad, recommendations, onSelectTicker }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const allWatchlist = recommendations?.signals || [];
  const groups = recommendations?.summary || [];
  const meta = recommendations?.meta || {};
  const emptyTone = getDisplayTone(meta.empty_state?.tone);

  if (isFirstLoad) return <RecommendationsSkeleton />;
  if (!recommendations) return null;

  return (
    <Card sx={{ mb: 2, px: 0, pt: 2, pb: allWatchlist.length > 0 ? 0 : 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5, px: 2.5 }}>
        <Recommend sx={{ fontSize: 14, color: colors.gray[500] }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.gray[600] }}>{t('vsa.recommendations.title')}</Typography>
        <Chip label={allWatchlist.length} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha(colors.info.main, 0.08), color: colors.info.main }} />
      </Box>

      {meta.show_fallback && allWatchlist.length > 0 && (
        <Box sx={{ mx: 2.5, mb: 1.5, px: 1.5, py: 0.8, borderRadius: 1, bgcolor: alpha('#f59e0b', 0.06), border: '1px solid', borderColor: alpha('#f59e0b', 0.15), display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <RemoveRedEye sx={{ fontSize: 14, color: '#f59e0b' }} />
          <Typography sx={{ fontSize: '0.68rem', color: status.warning.text, fontWeight: 600 }}>{getDisplayText(meta.fallback, t)}</Typography>
        </Box>
      )}

      {allWatchlist.length === 0 ? (
        <Box sx={{ mx: 2.5, mt: 1.5, p: 2, borderRadius: 1.5, bgcolor: alpha(emptyTone.color, 0.04), border: `1px solid ${alpha(emptyTone.color, 0.12)}`, textAlign: 'center' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: alpha(emptyTone.color, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
            <Shield sx={{ fontSize: 24, color: emptyTone.color }} />
          </Box>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: colors.gray[800], mb: 0.5 }}>{t(meta.empty_state?.title_key)}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: colors.gray[500], maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}>{t(meta.empty_state?.body_key)}</Typography>
        </Box>
      ) : (
        <>
          {groups.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, px: 2.5, mb: 1.5, flexWrap: 'wrap' }}>
              {groups.map((group) => {
                const tone = getDisplayTone(group.display?.tone);
                const Icon = ICONS[group.display?.icon] || Schedule;
                return (
                  <Box key={group.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Icon sx={{ fontSize: 13, color: tone.color }} />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: tone.color }}>{group.count} {getDisplayText(group.display, t)}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: colors.gray[400] }}>- {getDisplayText({ key: group.display?.desc_key }, t, '')}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          {meta.show_regime_warning && (
            <Box sx={{ mx: 2.5, mb: 1.5, px: 1.5, py: 0.8, borderRadius: 1, bgcolor: alpha('#3b82f6', 0.06), border: '1px solid', borderColor: alpha('#3b82f6', 0.15), display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Speed sx={{ fontSize: 14, color: colors.info.main }} />
              <Typography sx={{ fontSize: '0.68rem', color: colors.info.main, fontWeight: 600 }}>{getDisplayText(meta.regime_warning, t)}</Typography>
            </Box>
          )}

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thSx, width: 90 }}>{t('vsa.recommendations.headers.status')}</TableCell>
                  <TableCell sx={thSx}>{t('vsa.recommendations.headers.ticker')}</TableCell>
                  <TableCell sx={thSx}>{t('vsa.recommendations.headers.sector')}</TableCell>
                  <TableCell sx={thSx}>{t('vsa.recommendations.headers.setup')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vsa.recommendations.headers.price')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vsa.recommendations.headers.entry')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vsa.recommendations.headers.stop')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('vsa.recommendations.headers.target')}</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('vsa.recommendations.headers.score')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allWatchlist.map((signal, index) => {
                  const statusDisplay = signal.display?.status || {};
                  const statusTone = getDisplayTone(statusDisplay.tone);
                  const StatusIcon = ICONS[statusDisplay.icon] || Schedule;
                  const tradePlan = signal.display?.trade_plan || {};
                  const priorityTone = getDisplayTone(signal.display?.priority?.tone);
                  return (
                    <TableRow key={index} hover sx={{ ...trSx(index, statusTone.color), cursor: 'pointer' }} onClick={() => onSelectTicker(signal.ticker)}>
                      <TableCell sx={tdSx}>
                        <Tooltip title={getDisplayText({ key: statusDisplay.tooltip_key }, t, '')} arrow placement="right">
                          <Chip icon={<StatusIcon sx={{ fontSize: '13px !important', color: `${statusTone.color} !important` }} />} label={getDisplayText(statusDisplay, t)} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 700, bgcolor: statusTone.bg, color: statusTone.color, border: `1px solid ${statusTone.border}`, cursor: 'help' }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: colors.gray[900] }}>{signal.ticker}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.7rem', color: colors.gray[500] }}>{signal.sector_name?.[lang] || signal.sector || '—'}</Typography></TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.7rem', color: colors.gray[600] }}>{getDisplayText(signal.display?.setup, t, signal.setup_type)}</Typography></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.gray[900] }}>{fmt(signal.close)}</Typography></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.info.main, fontWeight: 700 }}>{tradePlan.entry_mid ? fmt(tradePlan.entry_mid) : '—'}</Typography></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.error.main, fontWeight: 700 }}>{signal.stop_loss ? fmt(signal.stop_loss) : '—'}</Typography>
                          {tradePlan.risk_pct != null && <Typography sx={{ fontSize: '0.58rem', color: colors.gray[400] }}>{tradePlan.risk_pct.toFixed(1)}%</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.success.main, fontWeight: 700 }}>{tradePlan.target_value ? fmt(tradePlan.target_value) : '—'}</Typography></TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Typography sx={{ fontSize: '0.78rem', fontWeight: 800, fontFamily: 'monospace', color: priorityTone.color }}>{Math.round(signal.priority_score)}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Card>
  );
}
